import React, { useState, useEffect, useImperativeHandle } from 'react';
import { Plus, Trash2, FileDown } from 'lucide-react';
import { generateOPUPDF } from '../../../../utils/workflowPDF';
import { useOPUSession } from '../../../../hooks/useOPUSession';
import BuscaDeAnimal from '../../shared/BuscaDeAnimal';
import { moverLinha } from '../../../../utils/moverLinha';
import { useArrastarLinhas, AlcaDeArrastar } from '../../shared/useArrastarLinhas';
import { ehTouro } from '../../../../utils/animais';
import {
  HORMONAL_PROTOCOLS,
  EMPTY_OPU_SESSION,
  EMPTY_DONOR,
  calculateRecoveryRate,
  generateTempId
} from '../../../../constants/workflowGranular';
import {
  perdasDaDoadoraOPU, descreverPerda, confirmarRemocao,
} from '../../../../utils/confirmarRemocao';

/**
 * Aba OPU - Aspiração Folicular COMPLETA
 * Layout tabular para desktop
 */
import EquipeDaEtapa, { equipeNoWorkflow } from '../../shared/EquipeDaEtapa';
import { sessaoDoLocal } from '../../../../data/clients';

const OPUTab = React.forwardRef(({ service, workflowData, onUpdate, laboratoryId, clients = [] }, ref) => {
  const { loadOPUData, saveOPUData } = useOPUSession(laboratoryId);

  const [opuData, setOpuData] = useState(EMPTY_OPU_SESSION);
  const [isInitialized, setIsInitialized] = useState(false);
  const equipe = equipeNoWorkflow(workflowData, onUpdate, 'opu');

  const [manualQuantity, setManualQuantity] = useState(service?.opu_quantity || 0);

  useEffect(() => {
    if (isInitialized || !laboratoryId || !service?.id) return;

    const init = async () => {
      // Try normalized tables first, fall back to legacy JSON
      const normalized = await loadOPUData(service?.id);
      if (normalized) {
        setOpuData(normalized);
      } else if (service?.opu_data?.donors) {
        // Legacy JSON fallback
        const migratedData = {
          ...service.opu_data,
          donors: service.opu_data.donors.map(donor => {
            if (!donor.animal_identifier && donor.rgd) {
              return { ...donor, animal_identifier: donor.rgd, rgd: undefined };
            }
            return donor;
          })
        };
        setOpuData(migratedData);
      } else {
        setOpuData({
          ...EMPTY_OPU_SESSION,
          session: {
            date: workflowData?.opu_date || service?.opu_scheduled_date || null,
            time: null,
            farm: sessaoDoLocal(service, clients).farm,
            technician_name: '',
            protocol: null
          },
          donors: [{ ...EMPTY_DONOR, id: generateTempId() }],
          general_observations: workflowData?.opu_observations || '',
          completed: workflowData?.opu_done || false
        });
      }
      setIsInitialized(true);
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service?.id, laboratoryId, isInitialized]);

  // Esqueceu a doadora 3 e lançou como 11: arrasta para a posição 3 e o # se
  // refaz. A ordem do array é o sort_order que o salvamento grava.
  const arrastar = useArrastarLinhas((de, para) => setOpuData(prev => ({
    ...prev,
    donors: moverLinha(prev.donors, de, para),
  })));

  const handleAddDonor = () => {
    setOpuData(prev => ({
      ...prev,
      donors: [...prev.donors, { ...EMPTY_DONOR, id: generateTempId() }]
    }));
  };

  const handleRemoveDonor = (index) => {
    const d = opuData.donors[index];
    const pode = confirmarRemocao(descreverPerda({
      nome: d?.animal_identifier,
      perdas: perdasDaDoadoraOPU(d),
    }));
    if (!pode) return;

    setOpuData(prev => ({
      ...prev,
      donors: prev.donors.filter((_, i) => i !== index)
    }));
  };

  const handleDonorChange = (index, field, value) => {
    setOpuData(prev => ({
      ...prev,
      donors: prev.donors.map((donor, i) => {
        if (i !== index) return donor;
        const updated = { ...donor, [field]: value };
        if (field === 'oocytes_recovered' || field === 'oocytes_viable') {
          const total = parseInt(updated.oocytes_recovered) || 0;
          const viable = parseInt(updated.oocytes_viable) || 0;
          updated.recovery_rate = calculateRecoveryRate(viable, total);
        }
        return updated;
      })
    }));
  };

  const handleSelectAnimal = (index, animal) => {
    handleDonorChange(index, 'animal_id', animal.id);
    handleDonorChange(index, 'animal_identifier', animal.identifier);
    handleDonorChange(index, 'breed', animal.breed);
    // `opu_donors.rgd` existe e é gravada, mas escolher a doadora na lista
    // nunca a preenchia: a coluna ficava vazia mesmo com o RGD cadastrado.
    handleDonorChange(index, 'rgd', animal.rgd || '');
  };

  const handleSelectBull = (index, bull) => {
    handleDonorChange(index, 'bull', bull.identifier);
  };

  const handleSessionChange = (field, value) => {
    setOpuData(prev => ({
      ...prev,
      session: { ...prev.session, [field]: value }
    }));
  };

  useEffect(() => {
    if (!isInitialized) return;

    // Save summary fields to services table
    onUpdate('opu_date', opuData.session.date);
    onUpdate('opu_observations', opuData.general_observations);
    onUpdate('opu_done', opuData.completed);
    const totalOocytes = opuData.donors.reduce((sum, d) => sum + (parseInt(d.oocytes_recovered) || 0), 0);
    const finalQuantity = opuData.donors.length === 0 ? manualQuantity : totalOocytes;
    onUpdate('opu_quantity', finalQuantity);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opuData, isInitialized, manualQuantity]);

  useImperativeHandle(ref, () => ({
    save: () => {
      if (!isInitialized || !service?.id) return Promise.resolve();
      return saveOPUData(service.id, opuData);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [isInitialized, opuData, service?.id]);

  return (
    <div className="p-4">
      {/* Cabecalho da Sessao */}
      <div className="mb-4 p-3 bg-slate-50 border border-slate-200 rounded-lg">
        <h4 className="font-semibold text-slate-900 mb-3 text-sm">Sessao OPU</h4>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Data *</label>
            <input
              type="date"
              className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={opuData.session.date || ''}
              onChange={(e) => handleSessionChange('date', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Horario</label>
            <input
              type="time"
              className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={opuData.session.time || ''}
              onChange={(e) => handleSessionChange('time', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Fazenda</label>
            <input
              type="text"
              placeholder="Nome da fazenda"
              className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={opuData.session.farm || ''}
              onChange={(e) => handleSessionChange('farm', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Protocolo Hormonal</label>
            <select
              className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={opuData.session.protocol || ''}
              onChange={(e) => handleSessionChange('protocol', e.target.value || null)}
            >
              <option value="">Selecione...</option>
              {Object.values(HORMONAL_PROTOCOLS).map(protocol => (
                <option key={protocol} value={protocol}>{protocol}</option>
              ))}
            </select>
          </div>
          {/* A sessão de OPU guarda só o NOME do técnico (não há coluna para o
              id); o select acha o técnico pelo nome. */}
          <EquipeDaEtapa
            laboratoryId={laboratoryId}
            responsavel={opuData.session.technician_name ? { id: null, nome: opuData.session.technician_name } : null}
            planejado={isInitialized ? equipe.planejado : null}
            ajudante={equipe.ajudante}
            obrigatorio={false}
            onResponsavel={(id, nome) => {
              handleSessionChange('technician_name', nome);
              equipe.responsavelNaEquipe(id, nome);
            }}
            onAjudante={equipe.ajudanteNaEquipe}
          />
        </div>
      </div>

      {/* Tabela de Doadoras */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-semibold text-slate-900 text-sm">Doadoras</h4>
          <div className="flex items-center gap-2">
            <button
              onClick={() => generateOPUPDF(service, opuData)}
              className="flex items-center gap-1 text-xs text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 px-2 py-1 rounded transition-colors"
              title="Exportar PDF da OPU"
            >
              <FileDown className="w-3.5 h-3.5" /> PDF
            </button>
          </div>
        </div>

        <div className="overflow-x-auto sm:overflow-visible sm:w-max sm:min-w-full rounded-lg border border-slate-200">
          <table className="w-full text-sm border-collapse">
            <thead className="sticky top-0 z-10">
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="px-2 py-2 text-left text-xs font-semibold text-slate-600 whitespace-nowrap">#</th>
                <th className="px-2 py-2 text-left text-xs font-semibold text-slate-600 whitespace-nowrap">Animal</th>
                <th className="px-2 py-2 text-left text-xs font-semibold text-slate-600 whitespace-nowrap">Raca</th>
                <th className="px-2 py-2 text-left text-xs font-semibold text-slate-600 whitespace-nowrap">Touro</th>
                <th className="px-2 py-2 text-left text-xs font-semibold text-slate-600 whitespace-nowrap">Hora Asp.</th>
                <th className="px-2 py-2 text-center text-xs font-semibold text-slate-600 whitespace-nowrap">Viáveis</th>
                <th className="px-2 py-2 text-center text-xs font-semibold text-slate-600 whitespace-nowrap">Total</th>
                <th className="px-2 py-2 text-center text-xs font-semibold text-slate-600 whitespace-nowrap">Taxa%</th>
                <th className="px-2 py-2 text-left text-xs font-semibold text-slate-600 whitespace-nowrap">Obs</th>
                <th className="px-2 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
                {opuData.donors.map((donor, index) => {
                  const { realce, ...linhaArrastavel } = arrastar.linha(index);
                  return (
                  <tr key={donor.id} {...linhaArrastavel} className={`hover:bg-slate-50 ${realce}`}>
                    {/* # e a alça de arrastar */}
                    <td className="px-1 py-1.5 text-xs text-slate-500 font-medium whitespace-nowrap">
                      <span className="inline-flex items-center gap-0.5">
                        <AlcaDeArrastar props={arrastar.alca(index, opuData.donors.length)} rotulo="doadora" />
                        {index + 1}
                      </span>
                    </td>

                    {/* Animal - autocomplete */}
                    <td className="px-2 py-1.5 relative" style={{ minWidth: '145px' }}>
                      <BuscaDeAnimal
                        laboratoryId={laboratoryId}
                        valor={donor.animal_identifier}
                        onDigitar={(t) => handleDonorChange(index, 'animal_identifier', t)}
                        onEscolher={(animal) => handleSelectAnimal(index, animal)}
                        categoriaPadrao="Doadora"
                        clients={clients}
                        clientIdPadrao={service?.client_id}
                      />
                    </td>

                    {/* Raca */}
                    <td className="px-2 py-1.5" style={{ minWidth: '100px' }}>
                      <input
                        type="text"
                        placeholder="Ex: Gir"
                        className="w-full px-2 py-1 border border-slate-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                        value={donor.breed || ''}
                        onChange={(e) => handleDonorChange(index, 'breed', e.target.value)}
                      />
                    </td>

                    {/* Touro - autocomplete */}
                    <td className="px-2 py-1.5 relative" style={{ minWidth: '145px' }}>
                      <BuscaDeAnimal
                        laboratoryId={laboratoryId}
                        valor={donor.bull}
                        onDigitar={(t) => handleDonorChange(index, 'bull', t)}
                        onEscolher={(bull) => handleSelectBull(index, bull)}
                        filtro={ehTouro}
                        categoriaPadrao="Reprodutor"
                        clients={clients}
                        clientIdPadrao={service?.client_id}
                      />
                    </td>

                    {/* Hora Aspiracao */}
                    <td className="px-2 py-1.5" style={{ minWidth: '90px' }}>
                      <input
                        type="time"
                        className="w-full px-2 py-1 border border-slate-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                        value={donor.aspiration_time || ''}
                        onChange={(e) => handleDonorChange(index, 'aspiration_time', e.target.value)}
                      />
                    </td>

                    {/* Oocitos Viaveis */}
                    <td className="px-2 py-1.5" style={{ minWidth: '65px' }}>
                      <input
                        type="number" min="0" placeholder="0"
                        className="w-full px-1 py-1 border border-slate-300 rounded text-xs text-center focus:outline-none focus:ring-1 focus:ring-blue-500"
                        value={donor.oocytes_viable ?? ''}
                        onChange={(e) => handleDonorChange(index, 'oocytes_viable', e.target.value)}
                      />
                    </td>

                    {/* Total (inclui desnudos) */}
                    <td className="px-2 py-1.5" style={{ minWidth: '65px' }}>
                      <input
                        type="number" min="0" placeholder="0"
                        className="w-full px-1 py-1 border border-slate-300 rounded text-xs text-center focus:outline-none focus:ring-1 focus:ring-blue-500"
                        value={donor.oocytes_recovered ?? ''}
                        onChange={(e) => handleDonorChange(index, 'oocytes_recovered', e.target.value)}
                      />
                    </td>

                    {/* Taxa% */}
                    <td className="px-2 py-1.5" style={{ minWidth: '58px' }}>
                      <div className="w-full px-1 py-1 border border-slate-200 rounded text-xs text-center bg-slate-100 text-slate-600">
                        {donor.recovery_rate ? `${donor.recovery_rate}%` : '-'}
                      </div>
                    </td>

                    {/* Observacoes */}
                    <td className="px-2 py-1.5" style={{ minWidth: '130px' }}>
                      <input
                        type="text"
                        placeholder="Obs..."
                        className="w-full px-2 py-1 border border-slate-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                        value={donor.observations || ''}
                        onChange={(e) => handleDonorChange(index, 'observations', e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') { e.preventDefault(); handleAddDonor(); }
                        }}
                      />
                    </td>

                    {/* Remover */}
                    <td className="px-2 py-1.5">
                      <button
                        onClick={() => handleRemoveDonor(index)}
                        className="text-slate-300 hover:text-red-600 transition-colors p-0.5"
                        title="Remover doadora"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                  );
                })}
                {/* + Adicionar linha */}
                <tr>
                  <td colSpan={10} className="px-2 py-1.5">
                    <button
                      onClick={handleAddDonor}
                      className="flex items-center gap-1 text-slate-400 hover:text-slate-700 text-xs transition-colors"
                    >
                      <Plus className="w-3 h-3" />
                      Adicionar linha
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
      </div>

      {/* Observacoes Gerais */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-slate-700 mb-1">Observacoes Gerais</label>
        <textarea
          rows="2"
          placeholder="Observacoes gerais sobre a sessao OPU..."
          className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={opuData.general_observations || ''}
          onChange={(e) => setOpuData(prev => ({ ...prev, general_observations: e.target.value }))}
        />
      </div>

      {/* Campo de quantidade manual */}
      {opuData.donors.length === 0 && (
        <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg mb-4">
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Quantidade de Ovulos/Oocitos (Manual)
          </label>
          <p className="text-xs text-slate-500 mb-3">
            Este servico nao possui doadoras detalhadas. Preencha manualmente a quantidade total de ovulos coletados.
          </p>
          <input
            type="number" min="0" step="1" placeholder="Ex: 15"
            className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={manualQuantity || ''}
            onChange={(e) => setManualQuantity(parseInt(e.target.value) || 0)}
          />
        </div>
      )}

      {/* Resumo automatico */}
      {opuData.donors.length > 0 && (
        <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg mb-4">
          <p className="text-sm text-slate-700">
            <strong>{opuData.donors.reduce((sum, d) => sum + (parseInt(d.oocytes_viable) || 0), 0)} viáveis</strong>
            {' / '}
            <strong>{opuData.donors.reduce((sum, d) => sum + (parseInt(d.oocytes_recovered) || 0), 0)} total</strong>
            {' de '}
            <strong>{opuData.donors.length} doadora(s)</strong>
          </p>
        </div>
      )}

      {/* Checkbox Concluido */}
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="opu_completed"
          checked={opuData.completed || false}
          onChange={(e) => setOpuData(prev => ({ ...prev, completed: e.target.checked }))}
          className="form-checkbox h-5 w-5 text-blue-600"
        />
        <label htmlFor="opu_completed" className="text-sm font-medium text-slate-700">
          Marcar sessao OPU como concluida
        </label>
      </div>


    </div>
  );
});

export default OPUTab;
